import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AlertTriangle, Check, ChevronDown, Loader2, Search, X } from 'lucide-react';
import { PayerDirectoryService, PayerOption } from '../../services/PayerDirectoryService';

/**
 * Searchable payer selector, replacing the free-text insurance provider field.
 *
 * Free text cannot be verified — "BCBS", "Blue Cross", and "Blue Cross Blue
 * Shield of IL" are three different strings and only one maps to a payer we can
 * actually query. This binds the choice to a real trading partner ID.
 *
 * Implements the ARIA combobox pattern: full keyboard navigation, and the
 * highlighted option is announced via aria-activedescendant.
 */

interface PayerPickerProps {
  value?: PayerOption | null;
  onChange: (payer: PayerOption | null) => void;
  label?: string;
  /** Rendered as the input's id; also links the label and error text. */
  id?: string;
  required?: boolean;
  disabled?: boolean;
  error?: string;
  placeholder?: string;
}

const COLORS = {
  panel: '#1c2536',
  input: '#111827',
  border: '#374151',
  borderFocus: '#6CA6C1',
  text: '#f9fafb',
  label: '#d1d5db',
  muted: '#9ca3af',
  accent: '#6CA6C1',
  danger: '#ef4444',
  warning: '#D4A017',
  highlight: '#28527A',
};

const PayerPicker: React.FC<PayerPickerProps> = ({
  value,
  onChange,
  label = 'Insurance Payer',
  id = 'payer-picker',
  required = false,
  disabled = false,
  error,
  placeholder = 'Search by payer name or ID…',
}) => {
  const [term, setTerm] = useState('');
  const [options, setOptions] = useState<PayerOption[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);

  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  const listboxId = `${id}-listbox`;
  const errorId = `${id}-error`;

  const runSearch = useCallback(async (search: string) => {
    setLoading(true);
    try {
      setOptions(await PayerDirectoryService.search(search));
    } finally {
      setLoading(false);
    }
  }, []);

  // Debounced so typing does not re-filter thousands of payers per keystroke.
  useEffect(() => {
    if (!open) return undefined;
    const timer = setTimeout(() => runSearch(term), 150);
    return () => clearTimeout(timer);
  }, [term, open, runSearch]);

  // Close on outside click.
  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [open]);

  // Keep the highlighted option scrolled into view for keyboard users.
  useEffect(() => {
    if (activeIndex < 0 || !listRef.current) return;
    const node = listRef.current.children[activeIndex] as HTMLElement | undefined;
    node?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex]);

  const select = (payer: PayerOption) => {
    onChange(payer);
    setTerm('');
    setOpen(false);
    setActiveIndex(-1);
    inputRef.current?.focus();
  };

  const clear = () => {
    onChange(null);
    setTerm('');
    setActiveIndex(-1);
    inputRef.current?.focus();
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      if (!open) {
        setOpen(true);
        return;
      }
      setActiveIndex((current) => {
        if (!options.length) return -1;
        const next = event.key === 'ArrowDown' ? current + 1 : current - 1;
        if (next < 0) return options.length - 1;
        if (next >= options.length) return 0;
        return next;
      });
      return;
    }

    if (event.key === 'Enter' && open && activeIndex >= 0 && options[activeIndex]) {
      event.preventDefault();
      select(options[activeIndex]);
      return;
    }

    if (event.key === 'Escape' && open) {
      event.preventDefault();
      setOpen(false);
      setActiveIndex(-1);
      return;
    }

    if (event.key === 'Home' && open) {
      event.preventDefault();
      setActiveIndex(0);
    }

    if (event.key === 'End' && open) {
      event.preventDefault();
      setActiveIndex(options.length - 1);
    }
  };

  const unverifiedId = value?.source === 'static';

  const inputValue = useMemo(() => (open ? term : value?.name || ''), [open, term, value]);

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      <label
        htmlFor={id}
        style={{ display: 'block', marginBottom: '0.5rem', color: COLORS.label, fontSize: '14px' }}
      >
        {label}
        {required && (
          <span style={{ color: COLORS.danger, marginLeft: '4px' }} aria-hidden="true">
            *
          </span>
        )}
      </label>

      <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
        <Search
          size={16}
          aria-hidden="true"
          style={{ position: 'absolute', left: '12px', color: COLORS.muted, pointerEvents: 'none' }}
        />

        <input
          ref={inputRef}
          id={id}
          type="text"
          role="combobox"
          aria-expanded={open}
          aria-controls={listboxId}
          aria-autocomplete="list"
          aria-required={required}
          aria-invalid={Boolean(error)}
          aria-describedby={error ? errorId : undefined}
          aria-activedescendant={
            open && activeIndex >= 0 ? `${id}-option-${activeIndex}` : undefined
          }
          autoComplete="off"
          disabled={disabled}
          placeholder={value && !open ? value.name : placeholder}
          value={inputValue}
          onChange={(event) => {
            setTerm(event.target.value);
            setOpen(true);
            setActiveIndex(-1);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={handleKeyDown}
          style={{
            width: '100%',
            padding: '10px 68px 10px 36px',
            background: COLORS.input,
            border: `1px solid ${error ? COLORS.danger : COLORS.border}`,
            borderRadius: '0.5rem',
            color: COLORS.text,
            fontSize: '14px',
            outline: 'none',
          }}
        />

        <div
          style={{
            position: 'absolute',
            right: '10px',
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
          }}
        >
          {loading && (
            <Loader2 size={14} aria-hidden="true" style={{ color: COLORS.muted }} className="spin" />
          )}
          {value && !disabled && (
            <button
              type="button"
              onClick={clear}
              aria-label={`Clear selected payer ${value.name}`}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: COLORS.muted,
                display: 'flex',
                padding: '2px',
              }}
            >
              <X size={14} aria-hidden="true" />
            </button>
          )}
          <ChevronDown size={16} aria-hidden="true" style={{ color: COLORS.muted }} />
        </div>
      </div>

      {/* A static-sourced payer has an unverified trading partner ID. Say so
          before the check fails, not after. */}
      {unverifiedId && (
        <p
          style={{
            margin: '6px 0 0',
            color: COLORS.warning,
            fontSize: '12px',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
          }}
        >
          <AlertTriangle size={12} aria-hidden="true" />
          Payer ID not yet verified against Stedi — verification may fail for this payer.
        </p>
      )}

      {error && (
        <p id={errorId} role="alert" style={{ margin: '6px 0 0', color: COLORS.danger, fontSize: '12px' }}>
          {error}
        </p>
      )}

      {open && (
        <ul
          ref={listRef}
          id={listboxId}
          role="listbox"
          aria-label="Insurance payers"
          style={{
            position: 'absolute',
            zIndex: 50,
            top: 'calc(100% + 4px)',
            left: 0,
            right: 0,
            maxHeight: '280px',
            overflowY: 'auto',
            margin: 0,
            padding: '4px',
            listStyle: 'none',
            background: COLORS.panel,
            border: `1px solid ${COLORS.border}`,
            borderRadius: '0.5rem',
            boxShadow: '0 12px 32px rgba(0,0,0,0.45)',
          }}
        >
          {!loading && options.length === 0 && (
            <li style={{ padding: '12px', color: COLORS.muted, fontSize: '13px' }}>
              No payers match “{term}”. Check the spelling, or search by payer ID.
            </li>
          )}

          {options.map((payer, index) => {
            const selected = value?.id === payer.id;
            const active = index === activeIndex;

            return (
              <li
                key={payer.id}
                id={`${id}-option-${index}`}
                role="option"
                aria-selected={selected}
                onMouseEnter={() => setActiveIndex(index)}
                onClick={() => select(payer)}
                style={{
                  padding: '8px 10px',
                  borderRadius: '0.375rem',
                  cursor: 'pointer',
                  background: active ? COLORS.highlight : 'transparent',
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '8px',
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      color: COLORS.text,
                      fontSize: '14px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                    }}
                  >
                    <span
                      style={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {payer.name}
                    </span>
                    {payer.isMbho && (
                      <span
                        style={{
                          backgroundColor: '#EDE7F6',
                          color: '#4527A0',
                          padding: '1px 6px',
                          borderRadius: '4px',
                          fontSize: '11px',
                          fontWeight: 500,
                          flexShrink: 0,
                        }}
                      >
                        Behavioral
                      </span>
                    )}
                  </div>
                  <div style={{ color: COLORS.muted, fontSize: '12px', marginTop: '2px' }}>
                    ID {payer.stediPayerId}
                    {payer.address ? ` · ${payer.address}` : ''}
                  </div>
                </div>
                {selected && <Check size={14} aria-hidden="true" style={{ color: COLORS.accent }} />}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default PayerPicker;
